import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AmenityGroupReturn, AmenityReturn } from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading, Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import { Badge } from '../Components/Catalyst/badge';
import HomeSelector from '../Components/Custom/HomeSelector';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import AmenityPhoto, { coverPhoto } from './AmenityPhoto';
import {
  feeSummary,
  formatClock,
  hoursByDay,
  isBookableMode,
  outOfSeasonText,
  parseLocal,
  todayIn,
  windowsOn,
} from './reservationFormat';
import { bookingBlockMessage, reservationErrorMessage } from './ruleMessages';

interface Props {
  currentPage: string;
}

/** Today's hours, in one short phrase, for a card. */
const todaysHours = (a: AmenityReturn): string => {
  if (a.booking_mode === 'overnight') {
    return a.check_in_minute !== null && a.check_in_minute !== undefined
      ? `Check in from ${formatClock(a.check_in_minute)}`
      : '';
  }
  const todayDate = todayIn(a.timezone);
  const { windows, outOfSeason } = windowsOn(a, todayDate);
  if (outOfSeason) return outOfSeasonText(a, todayDate);
  if (windows.length === 0) return 'Open all day';
  const today = parseLocal(todayDate);
  if (!today) return '';
  const weekday = new Date(
    Date.UTC(today.year, today.month - 1, today.day)
  ).getUTCDay();
  const line = hoursByDay(windows)[weekday];
  return line.text === 'Closed' ? 'Closed today' : `Today ${line.text}`;
};

const modeBadge = (a: AmenityReturn): React.ReactNode => {
  if (a.booking_mode === 'info_only') return <Badge color="zinc">Info only</Badge>;
  if (bookingBlockMessage(a)) return <Badge color="zinc">Not bookable for you</Badge>;
  if (!isBookableMode(a.booking_mode)) return <Badge color="zinc">Book with management</Badge>;
  if (a.approval_mode === 'manual') return <Badge color="amber">Requires approval</Badge>;
  return <Badge color="green">Book instantly</Badge>;
};

/**
 * Everything the association lets its households use — what can be
 * booked, and what is simply there to enjoy (#643). Scoped to the home
 * selected in the switcher, since amenities belong to its association.
 */
const AmenitiesPage: React.FC<Props> = ({ currentPage }) => {
  const { homes, selectedHome, loading: memberLoading } = useMember();
  const homeId = selectedHome?.home_id;
  const [amenities, setAmenities] = useState<AmenityReturn[]>([]);
  const [groups, setGroups] = useState<AmenityGroupReturn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!homeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Groups are a convenience; the page stands without them.
    memberReservationService
      .listMyAmenityGroups({ homeId })
      .then(({ data }) => {
        if (!cancelled) setGroups(data.groups.filter((g) => g.amenities.some((m) => m.is_active)));
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    memberReservationService
      .listMyAmenities({ homeId })
      .then(({ data }) => {
        if (!cancelled) setAmenities(data.amenities);
      })
      .catch((err) => {
        if (!cancelled) {
          setAmenities([]);
          setError(reservationErrorMessage(err, "Couldn't load the amenities."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [homeId]);

  if (memberLoading) return <LoadingSpinner />;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Heading>Amenities</Heading>
        <Link
          to="/reservations"
          className="text-sm text-sky-700 hover:underline dark:text-sky-400"
        >
          My reservations
        </Link>
      </div>

      {homes.length === 0 ? (
        <Subheading className="mt-2">
          Your account is not linked to a home yet. Please contact your
          association manager.
        </Subheading>
      ) : (
        <>
          <div className="mt-2">
            <HomeSelector />
          </div>

          <ErrorAlert className="mt-4">{error}</ErrorAlert>

          {groups.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {groups.map((g) => (
                <Link
                  key={g.id}
                  to={`/amenities/groups/${g.id}`}
                  className="rounded-full border border-sky-300 px-4 py-2 text-sm font-medium text-sky-800 hover:bg-sky-50 dark:border-sky-700 dark:text-sky-300 dark:hover:bg-sky-500/10"
                >
                  Book any of {g.name}
                </Link>
              ))}
            </div>
          )}

          {loading ? (
            <LoadingSpinner />
          ) : amenities.length === 0 && !error ? (
            <Text className="mt-6">
              Your association hasn't listed any amenities yet.
            </Text>
          ) : (
            <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {amenities.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/amenities/${a.id}`}
                    className="block overflow-hidden rounded-2xl border border-zinc-200 bg-white transition hover:border-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-zinc-700 dark:bg-gray-800 dark:hover:border-sky-500"
                  >
                    <AmenityPhoto
                      photo={coverPhoto(a.photos)}
                      alt={a.name}
                      className="h-36"
                    />
                    <div className="space-y-2 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="min-w-0 text-base font-semibold text-zinc-950 dark:text-white">
                          {a.name}
                        </h3>
                        <span className="shrink-0">{modeBadge(a)}</span>
                      </div>
                      {a.description && (
                        <p className="line-clamp-2 text-sm text-zinc-600 dark:text-zinc-400">
                          {a.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-500 dark:text-zinc-400">
                        <span>{todaysHours(a)}</span>
                        {a.booking_mode !== 'info_only' && <span>{feeSummary(a)}</span>}
                      </div>
                      {bookingBlockMessage(a) && (
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          {bookingBlockMessage(a)}
                        </p>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </SidebarLayout>
  );
};

export default AmenitiesPage;
